import React, { useMemo } from 'react'
import { CanvasObjectiveCard } from './CanvasObjectiveCard'
import { CanvasScorecardCell } from './CanvasScorecardCell'
import { CvpAttributeStrip } from './CanvasZonePlaceholders'
import {
  BSC_PERSPECTIVE_DESCRIPTIONS,
  IP_CLUSTER_DISPLAY,
  IP_CLUSTERS,
  LG_CAPITALS,
  partitionByCapital,
  partitionByCluster,
  partitionByTheme,
  type BscPerspective,
  type CanvasObjective,
  type PerspectiveScorecard,
} from './strategyMapHelpers'
import './CanvasPerspectiveRow.css'

interface Props {
  perspective: BscPerspective
  objectives: CanvasObjective[]
  scorecard: PerspectiveScorecard
  onMeasureClick?: (measure: string) => void
  onInitiativeClick?: (initiative: string) => void
  focusedCardId?: string | null
  chainNeighborIds?: Set<string>
  onCardClick?: (id: string) => void
}

const PERSPECTIVE_LABEL: Record<BscPerspective, string> = {
  'Financial': 'Financial Perspective',
  'Customer': 'Customer Perspective',
  'Internal Process': 'Internal Perspective',
  'Learning & Growth': 'Learning and Growth Perspective',
}

export const CanvasPerspectiveRow: React.FC<Props> = ({
  perspective,
  objectives,
  scorecard,
  onMeasureClick,
  onInitiativeClick,
  focusedCardId,
  chainNeighborIds,
  onCardClick,
}) => {
  const themePartition = useMemo(() => partitionByTheme(objectives), [objectives])
  const clusterPartition = useMemo(() => partitionByCluster(objectives), [objectives])
  const capitalPartition = useMemo(() => partitionByCapital(objectives), [objectives])

  const customerExpressed = useMemo(() => {
    const set = new Set<string>()
    for (const obj of objectives) {
      for (const attr of obj.cvpAttributes ?? []) set.add(attr)
    }
    return set
  }, [objectives])

  const renderProductivityGrowth = () => (
    <div className="canvas-kn-row-cards canvas-kn-row-cards--split">
      <div className="canvas-kn-row-col canvas-kn-row-col--productivity">
        {themePartition.productivity.length === 0 ? (
          <span className="canvas-kn-row-col-empty">— none —</span>
        ) : (
          themePartition.productivity.map(o => (
            <CanvasObjectiveCard
              key={o.id}
              objective={o}
              isFocused={focusedCardId === o.id}
              isInChain={!!focusedCardId && chainNeighborIds?.has(o.id)}
              isDeemphasized={
                !!focusedCardId && focusedCardId !== o.id && !chainNeighborIds?.has(o.id)
              }
              onClick={onCardClick ? () => onCardClick(o.id) : undefined}
            />
          ))
        )}
      </div>
      <div className="canvas-kn-row-col canvas-kn-row-col--growth">
        {themePartition.growth.length === 0 ? (
          <span className="canvas-kn-row-col-empty">— none —</span>
        ) : (
          themePartition.growth.map(o => (
            <CanvasObjectiveCard
              key={o.id}
              objective={o}
              isFocused={focusedCardId === o.id}
              isInChain={!!focusedCardId && chainNeighborIds?.has(o.id)}
              isDeemphasized={
                !!focusedCardId && focusedCardId !== o.id && !chainNeighborIds?.has(o.id)
              }
              onClick={onCardClick ? () => onCardClick(o.id) : undefined}
            />
          ))
        )}
      </div>
    </div>
  )

  const renderUnclassifiedFooter = (unclassified: CanvasObjective[]) =>
    unclassified.length > 0 ? (
      <div className="canvas-kn-row-unclassified-footer">
        {unclassified.map(o => (
          <CanvasObjectiveCard
            key={o.id}
            objective={o}
            isFocused={focusedCardId === o.id}
            isInChain={!!focusedCardId && chainNeighborIds?.has(o.id)}
            isDeemphasized={
              !!focusedCardId && focusedCardId !== o.id && !chainNeighborIds?.has(o.id)
            }
            onClick={onCardClick ? () => onCardClick(o.id) : undefined}
          />
        ))}
      </div>
    ) : null

  const renderIpClusterGrid = () => (
    <div className="canvas-kn-row-cards canvas-kn-row-cards--cluster-grid">
      {IP_CLUSTERS.map(cluster => {
        const cards = clusterPartition.byCluster[cluster]
        const isEmpty = cards.length === 0
        return (
          <div key={cluster} className="canvas-kn-row-col">
            <div
              className={`canvas-kn-row-col-header${isEmpty ? ' canvas-kn-row-col-header--empty' : ''}`}
            >
              {IP_CLUSTER_DISPLAY[cluster]}
            </div>
            {isEmpty ? (
              <span className="canvas-kn-row-col-empty">— none —</span>
            ) : (
              cards.map(o => (
                <CanvasObjectiveCard
                  key={o.id}
                  objective={o}
                  isFocused={focusedCardId === o.id}
                  isInChain={!!focusedCardId && chainNeighborIds?.has(o.id)}
                  isDeemphasized={
                    !!focusedCardId && focusedCardId !== o.id && !chainNeighborIds?.has(o.id)
                  }
                  onClick={onCardClick ? () => onCardClick(o.id) : undefined}
                />
              ))
            )}
          </div>
        )
      })}
    </div>
  )

  const renderLgCapitalGrid = () => (
    <div className="canvas-kn-row-cards canvas-kn-row-cards--capital-grid">
      {LG_CAPITALS.map(capital => {
        const cards = capitalPartition.byCapital[capital]
        const isEmpty = cards.length === 0
        return (
          <div key={capital} className="canvas-kn-row-col">
            <div
              className={`canvas-kn-row-col-header${isEmpty ? ' canvas-kn-row-col-header--empty' : ''}`}
            >
              {capital}
            </div>
            {isEmpty ? (
              <span className="canvas-kn-row-col-empty">— none —</span>
            ) : (
              cards.map(o => (
                <CanvasObjectiveCard
                  key={o.id}
                  objective={o}
                  isFocused={focusedCardId === o.id}
                  isInChain={!!focusedCardId && chainNeighborIds?.has(o.id)}
                  isDeemphasized={
                    !!focusedCardId && focusedCardId !== o.id && !chainNeighborIds?.has(o.id)
                  }
                  onClick={onCardClick ? () => onCardClick(o.id) : undefined}
                />
              ))
            )}
          </div>
        )
      })}
    </div>
  )

  const renderBody = () => {
    if (perspective === 'Internal Process') {
      return (
        <>
          {renderIpClusterGrid()}
          {renderUnclassifiedFooter(clusterPartition.unclassified)}
        </>
      )
    }
    if (perspective === 'Learning & Growth') {
      return (
        <>
          {renderLgCapitalGrid()}
          {renderUnclassifiedFooter(capitalPartition.unclassified)}
        </>
      )
    }
    // Financial or Customer
    return (
      <>
        {perspective === 'Customer' && (
          <>
            <div className="canvas-kn-cvp-section-label">Customer Value Proposition</div>
            <CvpAttributeStrip expressed={customerExpressed} />
          </>
        )}
        {renderProductivityGrowth()}
        {renderUnclassifiedFooter(themePartition.unclassified)}
      </>
    )
  }

  return (
    <div className="canvas-kn-perspective-row" data-perspective={perspective}>
      <div className="canvas-kn-perspective-label-cell">
        <div className="canvas-kn-perspective-label">{PERSPECTIVE_LABEL[perspective]}</div>
        <div className="canvas-kn-perspective-description">
          {BSC_PERSPECTIVE_DESCRIPTIONS[perspective]}
        </div>
      </div>
      <div className="canvas-kn-perspective-body">{renderBody()}</div>
      <CanvasScorecardCell
        items={scorecard.measures}
        variant="measures"
        onItemClick={onMeasureClick}
      />
      <CanvasScorecardCell items={scorecard.targets} variant="targets" />
      <CanvasScorecardCell
        items={scorecard.initiatives}
        variant="initiatives"
        onItemClick={onInitiativeClick}
      />
    </div>
  )
}
